import { lazy, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { useBoolean } from 'usehooks-ts';
import { useSafeMediaUrl } from '@/hooks/use-safe-media-url';
import cn from '@/utils/merge-class-names';
import '@cyntler/react-doc-viewer/dist/index.css';

const DocViewer = lazy(() => import('@cyntler/react-doc-viewer'));

interface Props {
  src: string;
  className?: string;
}

export function FileViewer({ className, src }: Pick<Props, 'src' | 'className'>) {
  const { t } = useTranslation();
  const [url = '', filename = '', fileSize = ''] = src.split('|');
  const { src: safeSrc } = useSafeMediaUrl(url);
  const { value: show, setTrue: setShow } = useBoolean(false);

  return show ? (
    <Suspense fallback={<div>Loading...</div>}>
      <DocViewer
        className={cn(
          'max-h-[calc(100dvh-8rem)] w-full max-w-[60rem] overflow-auto bg-neutral-0 [&_#proxy-renderer]:h-full [&_#proxy-renderer>div]:h-full',
          className,
        )}
        documents={[{ uri: safeSrc }]}
        config={{
          pdfVerticalScrollByDefault: true,
          header: { disableHeader: true },
        }}
      />
    </Suspense>
  ) : (
    <div className='flex flex-col gap-8 items-center justify-center bg-neutral-0 p-6'>
      <h5 className='break-all'>{filename || url.split('/').at(-1)}</h5>
      {fileSize && <span className='text-sm'>{fileSize}</span>}
      <button
        type='button'
        className='btn btn-outline btn-neutral'
        onClick={() => setShow()}>
        {t('conversation.media.show')}
      </button>
    </div>
  );
}
